// Top-level app shell — header nav + content outlet.
// Wraps every page with consistent chrome: branding, nav links, Jemma drawer,
// FlagsBell, Microsoft auth pill, Firebase signed-in pill.

import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import type { User } from 'firebase/auth';
import { JemmaDrawer } from './JemmaDrawer';
import { MicrosoftAuthButton } from './MicrosoftAuthButton';
import { FlagsBell } from './FlagsBell';
import { config } from '@/shared/config';
import {
  observeAuthState,
  signOut as firebaseSignOut,
  currentFirebaseUser,
} from '@/shared/firebase';

// Two-tier nav: daily-workflow group first (the path most clicks travel),
// utility group second (occasional pages — Settings is once a week, Dev is
// for diagnostics). Separator div renders between them.
const NAV_PRIMARY: Array<{ to: string; label: string }> = [
  { to: '/', label: 'Home' },
  { to: '/rfq', label: 'RFQ' },
  { to: '/customers', label: 'Customers' },
  { to: '/quotes', label: 'Quotes' },
  { to: '/parts', label: 'Parts' },
  { to: '/orders', label: 'Orders' },
];
const NAV_UTILITY: Array<{ to: string; label: string }> = [
  { to: '/settings', label: 'Settings' },
  { to: '/dev', label: 'Dev' },
];

/**
 * Header pill showing the signed-in Firebase user's email + a sign-out link.
 * Sits next to MicrosoftAuthButton at the same visual weight. AuthGate in
 * App.tsx guarantees a user is signed in by the time AppShell mounts, but we
 * still defend against the null case (e.g. mid-sign-out frame) by hiding the
 * pill entirely instead of rendering "Signed in as null".
 */
function FirebaseAuthPill() {
  const [user, setUser] = useState<User | null>(currentFirebaseUser());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return observeAuthState((u) => setUser(u));
  }, []);

  async function handleSignOut() {
    if (busy) return;
    setBusy(true);
    try {
      await firebaseSignOut();
      // AuthGate observes the same auth state and flips to LoginPage.
    } catch (err) {
      // Sign-out failures are rare; surface to console rather than blocking the UI.
      // eslint-disable-next-line no-console
      console.error('[FirebaseAuthPill] sign-out failed', err);
      setBusy(false);
    }
  }

  if (!user) return null;

  const email = user.email ?? user.uid;
  const displayEmail = email.length > 24 ? `${email.slice(0, 21)}…` : email;

  return (
    <div
      className="text-xs flex items-center gap-1.5 px-2 py-1 rounded bg-paper/10 border border-paper/20 text-white"
      title={`Signed in as ${email}`}
    >
      <span className="opacity-80">Signed in as</span>
      <span className="font-mono">{displayEmail}</span>
      <span className="opacity-40">·</span>
      <button
        type="button"
        onClick={handleSignOut}
        disabled={busy}
        className="text-orange hover:underline disabled:opacity-50"
      >
        {busy ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );
}

export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-paper text-text font-sans">
      <header className="bg-navy text-white shadow">
        {/* Top row: brand + auth/action cluster. The cluster wraps below
            the brand on <md screens so it doesn't blow out the row. */}
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 shrink-0">
            <NavLink to="/" className="font-bold text-lg">
              <span className="text-orange">AI</span> QUOTE
            </NavLink>
            <span className="text-xs bg-orange px-2 py-0.5 rounded font-mono">
              v{config.appVersion}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            <FlagsBell />
            <FirebaseAuthPill />
            <MicrosoftAuthButton />
            <button
              onClick={() => setDrawerOpen(!drawerOpen)}
              className="text-sm px-3 py-1 rounded bg-orange hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              {drawerOpen ? 'Close' : 'Talk to'} Jemma
            </button>
          </div>
        </div>

        {/* Nav row: scrolls horizontally on mobile so it never overflows
            the viewport. -webkit-overflow-scrolling lets iOS Safari momentum-
            scroll it. */}
        <nav className="px-4 sm:px-6 pb-2 flex items-center gap-1 overflow-x-auto">
          {NAV_PRIMARY.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `text-sm px-3 py-1 rounded transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-orange text-white'
                    : 'text-paper/80 hover:bg-paper/10 hover:text-white'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <span className="w-px h-5 bg-paper/20 mx-2 shrink-0" aria-hidden />
          {NAV_UTILITY.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `text-xs px-2 py-1 rounded transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-orange text-white'
                    : 'text-paper/60 hover:bg-paper/10 hover:text-white'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="p-4 sm:p-6">
        <Outlet />
      </main>

      <JemmaDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}
